import { useState } from 'react'
import {
  Folder,
  Briefcase,
  Rocket,
  Globe,
  Star,
  Zap,
  Heart,
  Code,
  Book,
  Music,
  Camera,
  Cpu,
  Database,
  Shield,
  Target,
  Truck,
  Home,
  Sun,
  Cloud,
  Award,
  Palette,
  FlaskConical,
  GraduationCap,
  ShoppingCart,
  Wrench,
  Gamepad2,
  Plane,
  Landmark,
  Megaphone,
  Leaf,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export const ICON_MAP: Record<string, LucideIcon> = {
  folder: Folder,
  briefcase: Briefcase,
  rocket: Rocket,
  globe: Globe,
  star: Star,
  zap: Zap,
  heart: Heart,
  code: Code,
  book: Book,
  music: Music,
  camera: Camera,
  cpu: Cpu,
  database: Database,
  shield: Shield,
  target: Target,
  truck: Truck,
  home: Home,
  sun: Sun,
  cloud: Cloud,
  award: Award,
  palette: Palette,
  'flask-conical': FlaskConical,
  'graduation-cap': GraduationCap,
  'shopping-cart': ShoppingCart,
  wrench: Wrench,
  'gamepad-2': Gamepad2,
  plane: Plane,
  landmark: Landmark,
  megaphone: Megaphone,
  leaf: Leaf,
}

const SIZE_CONFIG = {
  sm: { container: 'h-5 w-5', icon: 12, text: 'text-[10px]' },
  md: { container: 'h-7 w-7', icon: 16, text: 'text-xs' },
  lg: { container: 'h-10 w-10', icon: 20, text: 'text-sm' },
} as const

interface ProjectIconProps {
  name: string
  icon: string | null
  avatarUrl: string | null
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function ProjectIcon({ name, icon, avatarUrl, size = 'md', className }: ProjectIconProps) {
  const [imgError, setImgError] = useState(false)
  const config = SIZE_CONFIG[size]

  // Tier 1: Avatar image
  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        onError={() => setImgError(true)}
        className={cn(config.container, 'rounded-md object-cover flex-shrink-0', className)}
      />
    )
  }

  // Tier 2: Lucide icon
  const IconComponent = icon ? ICON_MAP[icon] : null
  if (IconComponent) {
    return (
      <span
        className={cn(
          config.container,
          'flex items-center justify-center rounded-md bg-muted flex-shrink-0',
          className
        )}
      >
        <IconComponent size={config.icon} className="text-muted-foreground" />
      </span>
    )
  }

  // Tier 3: Gray initial fallback
  const initial = name.charAt(0).toUpperCase() || '?'

  return (
    <span
      className={cn(
        config.container,
        config.text,
        'flex items-center justify-center rounded-md bg-muted font-semibold text-muted-foreground flex-shrink-0',
        className
      )}
    >
      {initial}
    </span>
  )
}
